import { createStore, type StoreApi } from 'zustand/vanilla';
import type { PostRow } from './postRows';
import { defaultSelection, memoPrefill, openingQuantity } from './postRows';

/**
 * PT-5 C6 — the Post dialog's editing state, ONE store per open dialog.
 *
 * All of it lives here, none on `PicklistDetailPage`, and each row subscribes to its own slice
 * (`useStore(store, (s) => s.memos[id])`). So a keystroke in a memo re-renders that row only; a To post change
 * re-renders that row and the figures that read quantities; a Reading change re-renders the rows of that item.
 * Before this, every keystroke re-rendered the whole page (the UI Performance gate's named red flag).
 *
 * Created once when the dialog opens and dropped when it closes, with the `ClientKey` (E7): a detail refresh
 * while the dialog is open (the page polls every few seconds while a batch is in flight) keeps every typed value
 * and the same key.
 */
export type GroupBy = 'none' | 'endorser' | 'department';

export interface PostDialogState {
  /** E7 — one key for every press while this dialog stays open; a reopened dialog is a new batch. */
  clientKey: string;
  selected: Readonly<Record<number, boolean>>;
  /** To post, as typed — kept as text so a blank or invalid value shows as an error rather than being clamped. */
  qtyText: Readonly<Record<number, string>>;
  memos: Readonly<Record<number, string>>;
  /** One reading per ITEM (anchor id), as typed. */
  readingText: Readonly<Record<number, string>>;
  /** Serial slots ticked (`picklistItemBinId`). */
  ticked: Readonly<Record<number, boolean>>;
  groupBy: GroupBy;
  lockedOpen: boolean;

  toggleRow: (id: number) => void;
  setRows: (ids: readonly number[], on: boolean) => void;
  /** Post only: exactly these rows, and grouped by endorser (the prototype's `onlyEndorser`). */
  onlyRows: (ids: readonly number[]) => void;
  setQty: (id: number, text: string) => void;
  setMemo: (id: number, text: string) => void;
  setReading: (anchorId: number, text: string) => void;
  setTicks: (ticks: Readonly<Record<number, boolean>>) => void;
  setGroupBy: (groupBy: GroupBy) => void;
  toggleLocked: () => void;
}

export interface PostDialogSeed {
  rows: readonly PostRow[];
  readingSeeds: Readonly<Record<number, number | null>>;
}

export function createPostDialogStore({ rows, readingSeeds }: PostDialogSeed): StoreApi<PostDialogState> {
  const initial = defaultSelection(rows);
  const readingText: Record<number, string> = {};
  for (const [anchor, seed] of Object.entries(readingSeeds)) readingText[Number(anchor)] = seed == null ? '' : String(seed);

  return createStore<PostDialogState>()((set) => ({
    clientKey: crypto.randomUUID(),
    selected: Object.fromEntries(initial.selected.map((id) => [id, true])),
    // ISS-R-117 — at the max, or at what the MRS still allows when that is less (owner 2026-10-06).
    qtyText: Object.fromEntries(rows.map((r) => [r.id, String(openingQuantity(r))])),
    // Posting defaults (ruling 09-25): the memo is pre-filled with the MRS line's remarks, clamped to the cap.
    memos: Object.fromEntries(rows.map((r) => [r.id, memoPrefill(r.remarks)])),
    readingText,
    // Every postable serial ticked on open, on unselected rows too (O-20 slot 63).
    ticked: Object.fromEntries(rows.flatMap((r) => r.serials.filter((u) => u.available).map((u) => [u.slotId, true]))),
    groupBy: initial.groupBy,
    lockedOpen: false,

    // The row checkbox NEVER changes serial ticks (ticket, explicit): unticking a row and ticking it back keeps
    // the serial choice the poster made.
    toggleRow: (id) => set((s) => ({ selected: { ...s.selected, [id]: !s.selected[id] } })),
    setRows: (ids, on) => set((s) => {
      const next = { ...s.selected };
      for (const id of ids) next[id] = on;
      return { selected: next };
    }),
    onlyRows: (ids) => set({ selected: Object.fromEntries(ids.map((id) => [id, true])), groupBy: 'endorser' }),
    setQty: (id, text) => set((s) => ({ qtyText: { ...s.qtyText, [id]: text } })),
    setMemo: (id, text) => set((s) => ({ memos: { ...s.memos, [id]: text } })),
    setReading: (anchorId, text) => set((s) => ({ readingText: { ...s.readingText, [anchorId]: text } })),
    setTicks: (ticks) => set((s) => ({ ticked: { ...s.ticked, ...ticks } })),
    setGroupBy: (groupBy) => set({ groupBy }),
    toggleLocked: () => set((s) => ({ lockedOpen: !s.lockedOpen })),
  }));
}

/** The typed To post as a number; NaN when blank or not a number (live validation shows it as an error). */
export function parseQty(text: string | undefined): number {
  if (text == null || text.trim() === '') return Number.NaN;
  return Number(text);
}

/** The typed reading as the wire value: null when blank, else the number. */
export function parseReading(text: string | undefined): number | null {
  if (text == null || text.trim() === '') return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}
